<template>
    <div class="text-sm">
        <Card paddingSize="medium">
            <template #content>
                <div
                    v-if="!isSingleReservation"
                    class="normal-case flex items-center mb-2 gap-1"
                >
                    <SchedulingStateBadge
                        :entityWithSchedulingState="attendanceData"
                    />
                    <PaymentStateBadge
                        v-if="reservationData.bookingPayment"
                        :entityWithPaymentState="reservationData"
                    />
                </div>
                <div class="space-y-8">
                    <div
                        class="grid grid-cols-12 gap-2 lg:gap-4 space-y-4 lg:space-y-0"
                    >
                        <div class="col-span-12 lg:col-span-4">
                            <MeetingDetailsBlock
                                iconName="user"
                                title="Attendee"
                            >
                                <div class="font-semibold">
                                    {{ reservationData.customer.getFullName() }}
                                </div>
                                <div>
                                    {{ reservationData.customer.email }}
                                </div>
                            </MeetingDetailsBlock>
                        </div>
                        <div class="col-span-12 lg:col-span-4">
                            <MeetingDetailsBlock
                                iconName="comment"
                                title="Notes"
                            >
                                <span
                                    v-if="
                                        reservationData.reservation.notes
                                            ?.length
                                    "
                                >
                                    {{ reservationData.reservation.notes }}
                                </span>
                                <span
                                    v-else
                                    class="text-gray-400"
                                >
                                    (empty)
                                </span>
                            </MeetingDetailsBlock>
                        </div>
                        <div class="col-span-12 lg:col-span-4">
                            <MeetingDetailsBlock
                                iconName="users"
                                title="Seats"
                            >
                                <span>
                                    {{
                                        reservationData.reservation.getNumberOfReservedSeatsLabel()
                                    }}
                                </span>
                            </MeetingDetailsBlock>
                        </div>
                    </div>

                    <BookingPaymentSection
                        v-if="reservationData.bookingPayment"
                        :bookingPayment="reservationData.bookingPayment"
                    />
                    <MeetingCustomFieldsSection
                        v-if="reservationData.hasCustomFieldResponses()"
                        :customFieldResponsesData="
                            reservationData.customFieldResponses
                        "
                    />

                    <div class="space-y-2">
                        <MeetingDetailsSectionTitle iconName="bolt">
                            Actions
                        </MeetingDetailsSectionTitle>
                        <div
                            class="flex flex-col md:flex-row items-center gap-1 w-full"
                        >
                            <AttendanceRescheduleButton
                                :meeting="meeting"
                                :reservation="reservationData.reservation"
                            />
                            <RefundReservationButtonModal
                                :meeting="meeting"
                                :reservation="reservationData.reservation"
                                @onSuccess="onRefundReservationSuccess"
                            />
                            <AttendanceToggleCompletedNoShowButtonModal
                                v-if="companyUser?.isAdminOrOwner()"
                                :attendance="attendanceData.attendance"
                                @onSuccess="onToggleNoShowAttendanceSuccess"
                            />
                            <AttendanceCancelButton
                                :meeting="meeting"
                                :attendanceData="attendanceData"
                                :reservationData="reservationData"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
    import { type PropType } from 'vue'
    import MeetingData from '@/App/Booking/Meeting/Entity/MeetingData'
    import Meeting from '@/App/Booking/Meeting/Entity/Meeting'
    import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
    import Card from '@/Shared/Component/Card.vue'
    import AttendanceRescheduleButton from '@/App/Booking/Meeting/Component/AttendanceRescheduleButton.vue'
    import RefundReservationButtonModal from '@/App/Booking/Meeting/Component/RefundReservationButtonModal.vue'
    import AttendanceCancelButton from '@/App/Booking/Meeting/Component/AttendanceCancelButton.vue'
    import SchedulingStateBadge from '@/App/Booking/Meeting/Component/SchedulingStateBadge.vue'
    import PaymentStateBadge from '@/App/Booking/Meeting/Component/PaymentStateBadge.vue'
    import MeetingDetailsBlock from '@/App/Booking/Meeting/Component/MeetingDetailsBlock.vue'
    import MeetingDetailsSectionTitle from '@/App/Booking/Meeting/Component/MeetingDetailsSectionTitle.vue'
    import BookingPaymentSection from '@/App/Booking/Meeting/Component/BookingPaymentSection.vue'
    import MeetingCustomFieldsSection from '@/App/Booking/Meeting/Component/MeetingCustomFieldsSection.vue'
    import AttendanceToggleCompletedNoShowButtonModal from '@/App/Booking/Meeting/Component/AttendanceToggleCompletedNoShowButtonModal.vue'
    import ReservationData from '@/App/Booking/Reservation/Entity/ReservationData'
    import AttendanceData from '@/App/Booking/Attendance/Entity/AttendanceData'

    const emits = defineEmits<{
        onRefundReservationSuccess: [{ meetingData: MeetingData }]
        onToggleNoShowAttendanceSuccess: [{ meetingData: MeetingData }]
    }>()

    const props = defineProps({
        meeting: {
            type: Object as PropType<Meeting>,
            required: true,
        },
        reservationData: {
            type: Object as PropType<ReservationData>,
            required: true,
        },
        attendanceData: {
            type: Object as PropType<AttendanceData>,
            required: true,
        },
        companyUser: {
            type: Object as PropType<CompanyUser | null>,
            default: null,
        },
        isSingleReservation: {
            type: Boolean,
            required: true,
        },
    })

    function onToggleNoShowAttendanceSuccess(event: {
        meetingData: MeetingData
    }): void {
        emits('onToggleNoShowAttendanceSuccess', {
            meetingData: event.meetingData,
        })
    }

    function onRefundReservationSuccess(event: {
        meetingData: MeetingData
    }): void {
        emits('onRefundReservationSuccess', {
            meetingData: event.meetingData,
        })
    }
</script>
